/**
 * DirectoryBrowserTab — paginated browser for the people, groups and devices
 * synced from any of the tenant's directory integrations. Settings shows it
 * as the Directory page, whose People / Groups / Devices tabs drive
 * `section` (ids stay `users` / `groups` / `devices`).
 *
 * Layout uses `SplitPage` so the per-entity detail opens in a resizable
 * right drawer, bound to the selection. The drawer has its own tabs:
 *   - Person detail → Overview, Groups, Devices
 *   - Group detail  → Overview, People, Devices, Sub-groups
 *   - Device detail → Overview, People, Groups
 *
 * Cross-entity navigation (clicking a group from inside a user
 * panel, or a user from inside a group's member list) switches the
 * active section *and* re-targets the drawer at the new entity.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    listDirectoryUsers,
    listDirectoryGroups,
    listDirectoryDevices,
    listDirectoryIntegrations,
    getDirectoryUser,
    getDirectoryGroup,
    getDirectoryDevice,
} from "../../lib/identityClient.js";
import { XMarkIcon } from "@heroicons/react/24/outline";
import OverflowTooltip from "../OverflowTooltip.jsx";
import DataTable from "../DataTable.jsx";
import SplitPage from "../SplitPage.jsx";
import Select from "../ui/Select.jsx";
import SearchInput from "../ui/SearchInput.jsx";
import Tabs from "../ui/Tabs.jsx";
import { Count } from "../ui/TableBar.jsx";
import Listing from "../ui/Listing.jsx";
import Notice, { ErrorText } from "../ui/Notice.jsx";
import Button from "../ui/Button.jsx";
import { SkeletonLines } from "../ui/Skeleton.jsx";
import { ICON_BUTTON } from "../ui/styles.js";
import { fullTimestampFormatter, timeLabelFormatter, dayLabelFormatter, parseServerTimestamp } from "../../lib/traceUtils.js";

const PROVIDER_OPTIONS = [
    { value: "",         label: "All providers" },
    { value: "ENTRA_ID", label: "Entra ID" },
    { value: "OKTA",     label: "Okta" },
    { value: "GOOGLE",   label: "Google" },
];

// What each section lists, in the reader's words. Settings calls the imported
// people "People"; the section id and the API keep `users`.
const SECTION_NOUN = {
    users:   { one: "person", many: "people" },
    groups:  { one: "group",  many: "groups" },
    devices: { one: "device", many: "devices" },
};

/**
 * What an in-flight sync makes untrue on this screen, for the provider filter in
 * force. A run streams rows in and retires departed ones only at the end, so a
 * mid-run screen mixes partial new rows, climbing counts, and prior-sync rows that
 * may already be gone upstream. `syncing` is the server's read-derived overlay.
 * `noun` names what the list holds ("people", "groups", "devices").
 */
export function syncingNotice(integrations, provider, noun) {
    const running = (integrations ?? []).filter(
        (i) => i?.status === "syncing" && (!provider || i.provider === provider),
    );
    if (!running.length) return null;
    const names = running.map((i) => i.displayName).filter(Boolean);
    const who = names.length === 1 ? names[0] : `${running.length} integrations`;
    // No completed run means nothing on screen is a prior state being refreshed.
    const firstEver = running.every(
        (i) => !i.lastFullSyncAt && !i.lastIncrementalSyncAt && !i.lastScopedSyncAt,
    );
    return firstEver
        ? `${who} is syncing for the first time. This list is filling in as it goes, so ${noun} and their counts are incomplete until it finishes.`
        : `${who} is syncing now. Counts are climbing, some ${noun} shown are from the previous sync, and anyone who has left the directory is still listed until the run finishes.`;
}

const PROVIDER_LABEL = {
    ENTRA_ID: "Entra ID",
    OKTA:     "Okta",
    GOOGLE:   "Google",
};

const PAGE_SIZE = 50;

// Start the next page while this many rows are still below the fold, so
// scrolling never stops at an empty tail.
const PREFETCH_ROWS = 12;
const NO_VISIBLE_RANGE = Object.freeze({ start: 0, end: 0, measured: false });

// The browser's per-host HTTP/1.1 ceiling.
const OWNER_RESOLVE_CONCURRENCY = 6;

// ── Shared cell helpers ──────────────────────────────────────────

function TimestampCell({ value }) {
    if (!value) return <span className="text-[color:var(--muted-soft)]">—</span>;
    const d = new Date(parseServerTimestamp(value));
    if (!Number.isFinite(d.getTime())) return <span className="text-[color:var(--muted-soft)]">—</span>;
    return (
        <div className="grid gap-0">
            <strong className="text-[11px] leading-[1.15] font-medium">{timeLabelFormatter.format(d)}</strong>
            <span className="text-[11px] leading-[1.15] text-[color:var(--muted-soft)]">{dayLabelFormatter.format(d)}</span>
        </div>
    );
}

function ProviderBadge({ provider }) {
    const label = PROVIDER_LABEL[provider] ?? provider;
    return (
        <span className="inline-flex max-w-full items-center rounded-[5px] border border-[color:var(--line)] px-[6px] py-[1px] text-[11px] text-[color:var(--muted)]">
            <span className="truncate" title={provider}>{label}</span>
        </span>
    );
}

function StatusBadge({ enabled }) {
    return (
        <span className={[
            "inline-flex items-center gap-[5px] min-w-0 text-[11px]",
            enabled ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
        ].join(" ")}>
            <span className={`h-[6px] w-[6px] flex-none rounded-[999px] ${enabled ? "bg-[color:var(--green)]" : "bg-[color:var(--muted-soft)]"}`} />
            <span className="truncate">{enabled ? "Active" : "Disabled"}</span>
        </span>
    );
}

// The directory names a platform by its enum (MAC_OS) and often repeats it in
// the version ("macOS 15.1"): say the platform once, in words.
const OS_NAMES = { MAC_OS: "macOS", WINDOWS: "Windows", LINUX: "Linux", IOS: "iOS", ANDROID: "Android", CHROME_OS: "ChromeOS" };

export function osLabel(os, version = "") {
    const raw = String(os || "");
    const name = OS_NAMES[raw.toUpperCase()] ?? raw.replace(/_/g, " ");
    const v = String(version || "").trim();
    if (!v) return name;
    if (!name || v.toLowerCase().replace(/\s/g, "").includes(name.toLowerCase().replace(/\s/g, ""))) return v;
    return `${name} ${v}`;
}

// ── Column schemas per section ───────────────────────────────────

// Columns share the list's width instead of scrolling it sideways: each takes
// `grow` parts of it and never less than `min` pixels. With a row's detail
// open, the narrower list keeps only its `primary` columns. A column with a
// `has` test is left out while no loaded row has a value for it (Google
// devices, for one, carry no name or serial).
const USER_COLS = [
    { id: "displayName", label: "Name",        min: 110, grow: 1.3, primary: true },
    { id: "email",       label: "Email",       min: 130, grow: 1.5, primary: true },
    { id: "provider",    label: "Provider",    min: 64,  grow: 0.6 },
    { id: "department",  label: "Department",  min: 80,  grow: 0.9, has: (u) => u.department },
    { id: "status",      label: "Status",      min: 60,  grow: 0.6 },
    { id: "title",       label: "Title",       min: 80,  grow: 1, has: (u) => u.title },
];

const GROUP_COLS = [
    { id: "displayName", label: "Group",       min: 140, grow: 2,   primary: true },
    { id: "provider",    label: "Provider",    min: 64,  grow: 0.7 },
    { id: "memberCount", label: "Members",     min: 64,  grow: 0.6, primary: true },
    { id: "syncedAt",    label: "Last synced", min: 80,  grow: 0.8 },
];

const DEVICE_COLS = [
    { id: "displayName",  label: "Name",         min: 110, grow: 1.2, primary: true, has: (d) => d.displayName },
    { id: "serialNumber", label: "Serial",       min: 90,  grow: 1, has: (d) => d.serialNumber },
    { id: "provider",     label: "Provider",     min: 64,  grow: 0.6 },
    { id: "os",           label: "OS",           min: 80,  grow: 0.8 },
    { id: "owner",        label: "Owner",        min: 120, grow: 1.4, primary: true },
];

// ── Detail tab schemas ───────────────────────────────────────────

const USER_DETAIL_TABS   = [{ id: "overview", label: "Overview" }, { id: "groups", label: "Groups" }, { id: "devices", label: "Devices" }];
const GROUP_DETAIL_TABS  = [{ id: "overview", label: "Overview" }, { id: "users", label: "People" }, { id: "devices", label: "Devices" }, { id: "subgroups", label: "Sub-groups" }];
const DEVICE_DETAIL_TABS = [{ id: "overview", label: "Overview" }, { id: "users", label: "People" }, { id: "groups", label: "Groups" }];

// Say what the list is rather than how another provider differs: only Entra writes
// group-to-device edges, so for the other two both of these are entirely
// owner-derived. Naming Entra here shows one customer another product's behaviour.
const DEVICE_GROUPS_VIA_OWNERS_HINT = "These are the groups this device's owners belong to.";
const GROUP_DEVICES_VIA_OWNERS_HINT = "These are devices registered to the users in this group.";

// `member_count` is the provider-reported total (users + devices +
// sub-groups), counted before the per-type split — so it can exceed the
// captured per-tab subsets. Tooltip on the Members column + Overview field.
const TOTAL_MEMBERS_HINT = "Total members: people, devices and sub-groups";

// ── Detail panel building blocks ─────────────────────────────────

function DetailField({ label, value, mono = false, hint }) {
    return (
        <div className="grid gap-[2px] min-w-0">
            <span className="text-[12px] leading-[1.3] text-[color:var(--muted)]" title={hint || undefined}>{label}</span>
            <span
                className={`text-[12px] leading-[1.32] text-[color:var(--ink)] [overflow-wrap:anywhere] ${mono ? "font-mono" : ""}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value || <span className="text-[color:var(--muted-soft)]">—</span>}
            </span>
        </div>
    );
}

function LinkRow({ label, meta, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full min-w-0 cursor-pointer items-center justify-between gap-[8px] rounded-[6px] border border-[color:var(--line)] bg-transparent px-[10px] py-[7px] text-left text-[12.5px] text-[color:var(--ink)] transition-colors hover:border-[color:var(--line-strong)] hover:bg-[color-mix(in_srgb,var(--ink)_5%,transparent)]"
        >
            <span className="truncate">{label}</span>
            {meta ? (
                <span className="shrink-0 text-[11.5px] text-[color:var(--muted)] tabular-nums">{meta}</span>
            ) : null}
        </button>
    );
}

// A drawer list still fetching: list-shaped bars, busy for assistive tech.
function DetailLoading({ label }) {
    return (
        <div aria-busy="true">
            <SkeletonLines lines={3} label={label} />
        </div>
    );
}

function DetailEmptyHint({ text }) {
    return <p className="m-0 py-[8px] text-[12.5px] leading-[1.45] text-[color:var(--muted)]">{text}</p>;
}

// A note over a drawer list saying where its rows come from.
function DetailHint({ text }) {
    return <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">{text}</p>;
}

// The drawer's head: what is open, and a close that clears the selection.
function DetailHeader({ title, onClose }) {
    return (
        <div className="flex min-h-[32px] items-center justify-between gap-[8px] border-l border-[color:var(--line-soft)] pl-[16px]">
            <h3 className="m-0 min-w-0 truncate text-[14px] font-medium leading-[1.3] text-[color:var(--ink)]" title={title}>{title}</h3>
            <button type="button" onClick={onClose} aria-label="Close details" title="Close details" className={ICON_BUTTON}>
                <XMarkIcon aria-hidden="true" className="h-[16px] w-[16px]" />
            </button>
        </div>
    );
}

// ── Section tab content (user / group / device) ──────────────────

function UserDetailContent({ selected, detail, loading, error, activeTab, navigateToGroup, navigateToDevice, onLoadMoreGroups, groupsLoading, onLoadMoreDevices, devicesLoading }) {
    if (activeTab === "overview") {
        return (
            <div className="grid gap-[12px]">
                <div className="grid gap-[10px] [grid-template-columns:repeat(2,minmax(0,1fr))]">
                    <DetailField label="Display name" value={selected.displayName} />
                    <DetailField label="Email" value={selected.email} />
                    <DetailField label="Provider" value={PROVIDER_LABEL[selected.sourceProvider] ?? selected.sourceProvider} />
                    <DetailField label="Status" value={selected.accountEnabled ? "Active" : "Disabled"} />
                    <DetailField label="Department" value={selected.department} />
                    <DetailField label="Title" value={selected.title} />
                    {selected.upn ? <DetailField label="UPN" value={selected.upn} /> : null}
                    <DetailField label="Source ID" value={selected.sourceId} mono />
                </div>
                {detail ? (
                    <div className="grid gap-[6px] border-t border-[color:var(--line)] pt-[10px]">
                        {detail.office ? <DetailField label="Office" value={detail.office} /> : null}
                        {detail.ou ? <DetailField label="OU" value={detail.ou} /> : null}
                        {detail.sid ? <DetailField label="SID" value={detail.sid} mono /> : null}
                        <DetailField label="Synced at" value={detail.syncedAt ? fullTimestampFormatter.format(parseServerTimestamp(detail.syncedAt)) : ""} />
                    </div>
                ) : loading ? (
                    <div className="border-t border-[color:var(--line)] pt-[10px]" aria-busy="true">
                        <SkeletonLines lines={2} label="Loading details…" />
                    </div>
                ) : null}
            </div>
        );
    }
    if (error) return <ErrorText>{error}</ErrorText>;
    if (activeTab === "devices") {
        if (loading && !detail) return <DetailLoading label="Loading devices…" />;
        if (!detail || !detail.devices?.length) return <DetailEmptyHint text="No devices are registered to this user." />;
        return (
            <ul className="m-0 grid list-none gap-[4px] p-0">
                {detail.devices.map((d) => (
                    <li key={`${d.sourceProvider}:${d.sourceId}`}>
                        <LinkRow
                            label={d.displayName || d.serialNumber || d.sourceId}
                            meta={[d.serialNumber, osLabel(d.operatingSystem)].filter(Boolean).join(", ")}
                            onClick={() => navigateToDevice(d)}
                        />
                    </li>
                ))}
                {detail.nextDevicePageToken ? <LoadMoreRow onClick={onLoadMoreDevices} loading={devicesLoading} /> : null}
            </ul>
        );
    }
    // Groups tab
    if (loading && !detail) return <DetailLoading label="Loading groups…" />;
    if (!detail || !detail.groups?.length) return <DetailEmptyHint text="Not a member of any groups." />;
    return (
        <ul className="m-0 grid list-none gap-[4px] p-0">
            {detail.groups.map((g) => (
                <li key={`${g.sourceProvider}:${g.sourceId}`}>
                    <LinkRow
                        label={g.displayName}
                        meta={`${g.memberCount} member${g.memberCount === 1 ? "" : "s"}`}
                        onClick={() => navigateToGroup(g)}
                    />
                </li>
            ))}
            {detail.nextGroupPageToken ? <LoadMoreRow onClick={onLoadMoreGroups} loading={groupsLoading} /> : null}
        </ul>
    );
}

// Loads itself when it scrolls into view. Every paginated drawer list ends with
// one, so they all page on scroll without threading a handler through each
// detail body. Falls back to a button where IntersectionObserver is missing.
function LoadMoreRow({ onClick, loading }) {
    const ref = useRef(null);
    const fire = useRef(onClick);
    fire.current = onClick;

    useEffect(() => {
        const node = ref.current;
        if (!node || loading || typeof IntersectionObserver !== "function") return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) fire.current?.();
        }, { rootMargin: "120px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [loading]);

    return (
        <li ref={ref} className="mt-[6px] text-center">
            <Button variant="ghost" size="sm" onClick={onClick} disabled={loading}>
                {loading ? "Loading…" : "Load more"}
            </Button>
        </li>
    );
}

function GroupDetailContent({ selected, detail, loading, error, activeTab, navigateToGroup, navigateToUser, navigateToDevice, onLoadMoreUsers, usersLoading, onLoadMoreDevices, devicesLoading, onLoadMoreSubGroups, subGroupsLoading }) {
    if (activeTab === "overview") {
        return (
            <div className="grid gap-[12px]">
                <div className="grid gap-[10px] [grid-template-columns:repeat(2,minmax(0,1fr))]">
                    <DetailField label="Group name" value={selected.displayName} />
                    <DetailField label="Provider" value={PROVIDER_LABEL[selected.sourceProvider] ?? selected.sourceProvider} />
                    <DetailField label="Members" value={String(selected.memberCount ?? 0)} hint={TOTAL_MEMBERS_HINT} />
                    <DetailField label="Last synced" value={selected.syncedAt ? fullTimestampFormatter.format(parseServerTimestamp(selected.syncedAt)) : ""} />
                    {selected.groupType ? <DetailField label="Type" value={selected.groupType} /> : null}
                    <DetailField label="Source ID" value={selected.sourceId} mono />
                </div>
                {selected.description ? (
                    <div className="grid gap-[6px] border-t border-[color:var(--line)] pt-[10px]">
                        <DetailField label="Description" value={selected.description} />
                    </div>
                ) : null}
            </div>
        );
    }
    if (error) return <ErrorText>{error}</ErrorText>;
    if (activeTab === "users") {
        if (loading && !detail) return <DetailLoading label="Loading people…" />;
        if (!detail || !detail.users?.length) return <DetailEmptyHint text="No people are direct members." />;
        return (
            <ul className="m-0 grid list-none gap-[4px] p-0">
                {detail.users.map((u) => (
                    <li key={`${u.sourceProvider}:${u.sourceId}`}>
                        <LinkRow
                            label={u.displayName || u.email || u.sourceId}
                            meta={u.email}
                            onClick={() => navigateToUser(u)}
                        />
                    </li>
                ))}
                {detail.nextUserPageToken ? <LoadMoreRow onClick={onLoadMoreUsers} loading={usersLoading} /> : null}
            </ul>
        );
    }
    if (activeTab === "devices") {
        if (loading && !detail) return <DetailLoading label="Loading devices…" />;
        // The list is the group's own device edges unioned with devices owned by
        // its members, so "no direct device members" was wrong for everyone and
        // named a category that does not exist outside Entra.
        const viaOwners = selected.sourceProvider !== "ENTRA_ID";
        if (!detail || !detail.devices?.length) {
            return (
                <DetailEmptyHint
                    text={
                        viaOwners
                            ? "None of this group's members have a device registered."
                            : "No devices in this group, and none registered to its members."
                    }
                />
            );
        }
        return (
            <div className="grid gap-[8px]">
                {viaOwners ? <DetailHint text={GROUP_DEVICES_VIA_OWNERS_HINT} /> : null}
                <ul className="m-0 grid list-none gap-[4px] p-0">
                    {detail.devices.map((d) => (
                        <li key={`${d.sourceProvider}:${d.sourceId}`}>
                            <LinkRow
                                label={d.displayName || d.serialNumber || d.sourceId}
                                meta={d.serialNumber || d.ownerDisplayName || (d.ownerRef?.[0] ?? "")}
                                onClick={() => navigateToDevice(d)}
                            />
                        </li>
                    ))}
                    {detail.nextDevicePageToken ? <LoadMoreRow onClick={onLoadMoreDevices} loading={devicesLoading} /> : null}
                </ul>
            </div>
        );
    }
    // sub-groups
    if (loading && !detail) return <DetailLoading label="Loading sub-groups…" />;
    if (!detail || !detail.subGroups?.length) return <DetailEmptyHint text="No direct sub-groups." />;
    return (
        <ul className="m-0 grid list-none gap-[4px] p-0">
            {detail.subGroups.map((g) => (
                <li key={`${g.sourceProvider}:${g.sourceId}`}>
                    <LinkRow
                        label={g.displayName}
                        meta={`${g.memberCount} member${g.memberCount === 1 ? "" : "s"}`}
                        onClick={() => navigateToGroup(g)}
                    />
                </li>
            ))}
            {detail.nextSubGroupPageToken ? <LoadMoreRow onClick={onLoadMoreSubGroups} loading={subGroupsLoading} /> : null}
        </ul>
    );
}

function DeviceDetailContent({ selected, detail, loading, error, activeTab, navigateToUser, navigateToGroup, onLoadMoreGroups, groupsLoading }) {
    if (activeTab === "overview") {
        const ownerLabel = (() => {
            if (selected.ownerDisplayName) return selected.ownerDisplayName;
            if (!selected.ownerRef?.length) return "";
            const isHuman = (s) => s.includes("@") || s.startsWith("CN=");
            const human = selected.ownerRef.filter(isHuman);
            const guids = selected.ownerRef.filter((s) => !isHuman(s));
            return [...human, ...guids].join(", ");
        })();
        return (
            <div className="grid gap-[12px]">
                <div className="grid gap-[10px] [grid-template-columns:repeat(2,minmax(0,1fr))]">
                    <DetailField label="Name" value={selected.displayName} />
                    <DetailField label="Provider" value={PROVIDER_LABEL[selected.sourceProvider] ?? selected.sourceProvider} />
                    <DetailField label="Serial" value={selected.serialNumber} mono />
                    <DetailField label="Operating system" value={osLabel(selected.operatingSystem, selected.osVersion)} />
                    <DetailField label="Owner" value={ownerLabel} />
                    <DetailField label="Last synced" value={selected.syncedAt ? fullTimestampFormatter.format(parseServerTimestamp(selected.syncedAt)) : ""} />
                    <DetailField label="Source ID" value={selected.sourceId} mono />
                </div>
            </div>
        );
    }
    if (error) return <ErrorText>{error}</ErrorText>;
    if (activeTab === "users") {
        if (loading && !detail) return <DetailLoading label="Loading people…" />;
        // `owners` is the subset of `ownerRef` that resolved to a synced user, so a
        // scoped sync leaving some unresolved is ordinary rather than an error.
        if (!detail || !detail.owners?.length) {
            return (
                <DetailEmptyHint
                    text={
                        selected.ownerRef?.length
                            ? "This device has owners, but none of them are in the synced directory."
                            : "No owner is registered on this device."
                    }
                />
            );
        }
        return (
            <ul className="m-0 grid list-none gap-[4px] p-0">
                {detail.owners.map((u) => (
                    <li key={`${u.sourceProvider}:${u.sourceId}`}>
                        <LinkRow
                            label={u.displayName || u.email || u.sourceId}
                            meta={u.email || ""}
                            onClick={() => navigateToUser(u)}
                        />
                    </li>
                ))}
            </ul>
        );
    }
    // Groups tab
    if (loading && !detail) return <DetailLoading label="Loading groups…" />;
    const viaOwners = selected.sourceProvider !== "ENTRA_ID";
    if (!detail || !detail.groups?.length) {
        return <DetailEmptyHint text={viaOwners ? "This device's owners are not in any groups." : "This device is not in any groups."} />;
    }
    return (
        <div className="grid gap-[8px]">
            {viaOwners ? <DetailHint text={DEVICE_GROUPS_VIA_OWNERS_HINT} /> : null}
            <ul className="m-0 grid list-none gap-[4px] p-0">
                {detail.groups.map((g) => (
                    <li key={`${g.sourceProvider}:${g.sourceId}`}>
                        <LinkRow
                            label={g.displayName}
                            meta={`${g.memberCount} member${g.memberCount === 1 ? "" : "s"}`}
                            onClick={() => navigateToGroup(g)}
                        />
                    </li>
                ))}
                {detail.nextGroupPageToken ? <LoadMoreRow onClick={onLoadMoreGroups} loading={groupsLoading} /> : null}
            </ul>
        </div>
    );
}

// ── Component ────────────────────────────────────────────────────

export default function DirectoryBrowserTab({ section = "users", focusHeader, onSectionChange }) {
    const [provider, setProvider] = useState("");
    const [search, setSearch] = useState("");

    const [users, setUsers] = useState([]);
    const [usersToken, setUsersToken] = useState(null);
    const [usersError, setUsersError] = useState(null);
    // Loading until the section's first read lands, so the list never
    // claims to be empty before it has asked.
    const [usersLoading, setUsersLoading] = useState(true);

    const [groups, setGroups] = useState([]);
    const [groupsToken, setGroupsToken] = useState(null);
    const [groupsError, setGroupsError] = useState(null);
    const [groupsLoading, setGroupsLoading] = useState(true);

    const [devices, setDevices] = useState([]);
    const [devicesToken, setDevicesToken] = useState(null);
    const [devicesError, setDevicesError] = useState(null);
    const [devicesLoading, setDevicesLoading] = useState(true);

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);

    const [userDetail, setUserDetail] = useState(null);
    const [userDetailLoading, setUserDetailLoading] = useState(false);
    const [userDetailError, setUserDetailError] = useState(null);
    const [groupDetail, setGroupDetail] = useState(null);
    const [groupDetailLoading, setGroupDetailLoading] = useState(false);
    const [groupDetailError, setGroupDetailError] = useState(null);
    // Refetched with the active list rather than polled: the same refetch that
    // brings finished rows in is what clears the banner.
    const [integrations, setIntegrations] = useState([]);

    const [deviceDetail, setDeviceDetail] = useState(null);
    const [deviceDetailLoading, setDeviceDetailLoading] = useState(false);
    const [deviceDetailError, setDeviceDetailError] = useState(null);
    const [groupUsersLoading, setGroupUsersLoading] = useState(false);
    const [groupDevicesLoading, setGroupDevicesLoading] = useState(false);
    const [groupSubGroupsLoading, setGroupSubGroupsLoading] = useState(false);
    const [userGroupsLoading, setUserGroupsLoading] = useState(false);
    const [userDevicesLoading, setUserDevicesLoading] = useState(false);
    const [deviceGroupsLoading, setDeviceGroupsLoading] = useState(false);

    // Drawer tab state. Reset to "overview" any time the selection
    // changes so we don't show a stale "Groups" tab against a
    // freshly-selected user that may not have its detail loaded yet.
    const [detailTab, setDetailTab] = useState("overview");
    useEffect(() => { setDetailTab("overview"); }, [selectedUser, selectedGroup, selectedDevice, section]);

    const [ownerCache, setOwnerCache] = useState({});

    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const usersRequest = useRef(0);
    const groupsRequest = useRef(0);
    const devicesRequest = useRef(0);

    const fetchUsers = useCallback(async (token) => {
        const request = ++usersRequest.current;
        const current = () => mountedRef.current && request === usersRequest.current;
        setUsersLoading(true);
        if (!token) setUsersError(null);
        try {
            const res = await listDirectoryUsers({
                provider: provider || undefined, search: search || undefined,
                page_size: PAGE_SIZE, page_token: token,
            });
            if (!current()) return;
            const list = res?.users ?? [];
            setUsers((prev) => (token ? [...prev, ...list] : list));
            setUsersToken(res?.nextPageToken ?? null);
        } catch (err) {
            if (!current()) return;
            setUsersError(`Failed to load people: ${err.message ?? String(err)}`);
        } finally {
            if (current()) setUsersLoading(false);
        }
    }, [provider, search]);

    const fetchGroups = useCallback(async (token) => {
        const request = ++groupsRequest.current;
        const current = () => mountedRef.current && request === groupsRequest.current;
        setGroupsLoading(true);
        if (!token) setGroupsError(null);
        try {
            const res = await listDirectoryGroups({
                provider: provider || undefined, search: search || undefined,
                page_size: PAGE_SIZE, page_token: token,
            });
            if (!current()) return;
            const list = res?.groups ?? [];
            setGroups((prev) => (token ? [...prev, ...list] : list));
            setGroupsToken(res?.nextPageToken ?? null);
        } catch (err) {
            if (!current()) return;
            setGroupsError(`Failed to load groups: ${err.message ?? String(err)}`);
        } finally {
            if (current()) setGroupsLoading(false);
        }
    }, [provider, search]);

    const fetchDevices = useCallback(async (token) => {
        const request = ++devicesRequest.current;
        const current = () => mountedRef.current && request === devicesRequest.current;
        setDevicesLoading(true);
        if (!token) setDevicesError(null);
        try {
            const res = await listDirectoryDevices({
                provider: provider || undefined, search: search || undefined,
                page_size: PAGE_SIZE, page_token: token,
            });
            if (!current()) return;
            const list = res?.devices ?? [];
            setDevices((prev) => (token ? [...prev, ...list] : list));
            setDevicesToken(res?.nextPageToken ?? null);
        } catch (err) {
            if (!current()) return;
            setDevicesError(`Failed to load devices: ${err.message ?? String(err)}`);
        } finally {
            if (current()) setDevicesLoading(false);
        }
    }, [provider, search]);

    useEffect(() => {
        if (section === "users")   fetchUsers();
        if (section === "groups")  fetchGroups();
        if (section === "devices") fetchDevices();
    }, [section, fetchUsers, fetchGroups, fetchDevices]);

    // owner_ref → user resolution for the Devices table.
    useEffect(() => {
        if (section !== "devices" || devices.length === 0) return;
        const toResolve = new Map();
        for (const d of devices) {
            for (const ref of d.ownerRef ?? []) {
                // Do not filter by ref shape. The query resolves an email, and
                // Google's owner_ref is always one.
                if (!ref) continue;
                const key = `${d.sourceProvider}:${ref}`;
                if (ownerCache[key]) continue;
                toResolve.set(key, { provider: d.sourceProvider, sourceId: ref });
            }
        }
        if (toResolve.size === 0) return;
        setOwnerCache((prev) => {
            const next = { ...prev };
            for (const key of toResolve.keys()) next[key] = "pending";
            return next;
        });
        // No cleanup flag: `ownerCache` is a dep, so cancelling on re-run would
        // kill the pool after its first resolution. The pending marks above are
        // what stop the re-entered effect starting a second one.
        const queue = [...toResolve.entries()];
        const resolveOne = async ([key, { provider: p, sourceId }]) => {
            try {
                const u = await getDirectoryUser(p, sourceId);
                setOwnerCache((prev) => ({ ...prev, [key]: u && u.sourceId ? u : "missing" }));
            } catch (err) {
                setOwnerCache((prev) => ({ ...prev, [key]: err?.status === 404 ? "missing" : "error" }));
            }
        };
        const drain = async () => {
            for (let entry = queue.shift(); entry; entry = queue.shift()) {
                await resolveOne(entry);
            }
        };
        // A worker shifts before its first await, so `queue.length` in the bound
        // would shrink as the pool starts.
        const workers = Math.min(OWNER_RESOLVE_CONCURRENCY, queue.length);
        for (let i = 0; i < workers; i += 1) drain();
    }, [section, devices, ownerCache]);

    // Best-effort: a failure here must not take the list down with it.
    useEffect(() => {
        let cancelled = false;
        listDirectoryIntegrations()
            .then((r) => { if (!cancelled) setIntegrations(r?.integrations ?? []); })
            .catch((err) => { console.warn("Failed to load integration status:", err); });
        return () => { cancelled = true; };
    }, [section, provider]);

    // Detail fetches.
    useEffect(() => {
        if (!selectedUser) { setUserDetail(null); setUserDetailError(null); return; }
        let cancelled = false;
        setUserDetailLoading(true);
        setUserDetailError(null);
        setUserDetail(null);
        getDirectoryUser(selectedUser.sourceProvider, selectedUser.sourceId, {
            groupPageSize: PAGE_SIZE,
            devicePageSize: PAGE_SIZE,
        })
            .then((d) => { if (!cancelled) setUserDetail(d); })
            .catch((err) => { if (!cancelled) setUserDetailError(`Failed to load this person: ${err.message ?? String(err)}`); })
            .finally(() => { if (!cancelled) setUserDetailLoading(false); });
        return () => { cancelled = true; };
    }, [selectedUser]);

    useEffect(() => {
        if (!selectedDevice) { setDeviceDetail(null); setDeviceDetailError(null); return; }
        let cancelled = false;
        setDeviceDetailLoading(true);
        setDeviceDetailError(null);
        setDeviceDetail(null);
        getDirectoryDevice({
            sourceProvider: selectedDevice.sourceProvider,
            sourceId: selectedDevice.sourceId,
            groupPageSize: PAGE_SIZE,
        })
            .then((d) => { if (!cancelled) setDeviceDetail(d); })
            .catch((err) => { if (!cancelled) setDeviceDetailError(`Failed to load device detail: ${err.message ?? String(err)}`); })
            .finally(() => { if (!cancelled) setDeviceDetailLoading(false); });
        return () => { cancelled = true; };
    }, [selectedDevice]);

    useEffect(() => {
        if (!selectedGroup) { setGroupDetail(null); setGroupDetailError(null); return; }
        let cancelled = false;
        setGroupDetailLoading(true);
        setGroupDetailError(null);
        setGroupDetail(null);
        getDirectoryGroup({
            sourceProvider: selectedGroup.sourceProvider,
            sourceId: selectedGroup.sourceId,
            userPageSize: PAGE_SIZE,
            devicePageSize: PAGE_SIZE,
            subGroupPageSize: PAGE_SIZE,
        })
            .then((d) => { if (!cancelled) setGroupDetail(d); })
            .catch((err) => { if (!cancelled) setGroupDetailError(`Failed to load group detail: ${err.message ?? String(err)}`); })
            .finally(() => { if (!cancelled) setGroupDetailLoading(false); });
        return () => { cancelled = true; };
    }, [selectedGroup]);

    const navigateToGroup = useCallback((g) => {
        onSectionChange?.("groups");
        setSelectedUser(null);
        setSelectedDevice(null);
        setSelectedGroup(g);
        setGroups((prev) => (prev.some((x) => x.sourceId === g.sourceId) ? prev : [g, ...prev]));
    }, [onSectionChange]);

    const navigateToUser = useCallback((u) => {
        onSectionChange?.("users");
        setSelectedGroup(null);
        setSelectedDevice(null);
        setSelectedUser(u);
        setUsers((prev) => (prev.some((x) => x.sourceId === u.sourceId) ? prev : [u, ...prev]));
    }, [onSectionChange]);

    const navigateToDevice = useCallback((d) => {
        onSectionChange?.("devices");
        setSelectedUser(null);
        setSelectedGroup(null);
        setSelectedDevice(d);
        setDevices((prev) => (prev.some((x) => x.sourceId === d.sourceId) ? prev : [d, ...prev]));
    }, [onSectionChange]);

    // Drawer "Load more": fetch the next user/device page and append. The
    // group RPC returns both lists each call, so we keep only the page we
    // asked for and leave the other tab's state untouched.
    const loadMoreGroupUsers = useCallback(async () => {
        if (!selectedGroup || !groupDetail?.nextUserPageToken || groupUsersLoading) return;
        setGroupUsersLoading(true);
        try {
            const res = await getDirectoryGroup({
                sourceProvider: selectedGroup.sourceProvider,
                sourceId: selectedGroup.sourceId,
                userPageToken: groupDetail.nextUserPageToken,
            });
            if (!mountedRef.current) return;
            setGroupDetail((prev) => (prev ? {
                ...prev,
                users: [...(prev.users ?? []), ...(res?.users ?? [])],
                nextUserPageToken: res?.nextUserPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more group users:", err);
        } finally {
            if (mountedRef.current) setGroupUsersLoading(false);
        }
    }, [selectedGroup, groupDetail, groupUsersLoading]);

    const loadMoreUserGroups = useCallback(async () => {
        if (!selectedUser || !userDetail?.nextGroupPageToken || userGroupsLoading) return;
        setUserGroupsLoading(true);
        try {
            const res = await getDirectoryUser(selectedUser.sourceProvider, selectedUser.sourceId, {
                groupPageToken: userDetail.nextGroupPageToken,
            });
            if (!mountedRef.current) return;
            setUserDetail((prev) => (prev ? {
                ...prev,
                groups: [...(prev.groups ?? []), ...(res?.groups ?? [])],
                nextGroupPageToken: res?.nextGroupPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more user groups:", err);
        } finally {
            if (mountedRef.current) setUserGroupsLoading(false);
        }
    }, [selectedUser, userDetail, userGroupsLoading]);

    const loadMoreUserDevices = useCallback(async () => {
        if (!selectedUser || !userDetail?.nextDevicePageToken || userDevicesLoading) return;
        setUserDevicesLoading(true);
        try {
            const res = await getDirectoryUser(selectedUser.sourceProvider, selectedUser.sourceId, {
                devicePageToken: userDetail.nextDevicePageToken,
            });
            if (!mountedRef.current) return;
            setUserDetail((prev) => (prev ? {
                ...prev,
                devices: [...(prev.devices ?? []), ...(res?.devices ?? [])],
                nextDevicePageToken: res?.nextDevicePageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more user devices:", err);
        } finally {
            if (mountedRef.current) setUserDevicesLoading(false);
        }
    }, [selectedUser, userDetail, userDevicesLoading]);

    const loadMoreDeviceGroups = useCallback(async () => {
        if (!selectedDevice || !deviceDetail?.nextGroupPageToken || deviceGroupsLoading) return;
        setDeviceGroupsLoading(true);
        try {
            const res = await getDirectoryDevice({
                sourceProvider: selectedDevice.sourceProvider,
                sourceId: selectedDevice.sourceId,
                groupPageToken: deviceDetail.nextGroupPageToken,
            });
            if (!mountedRef.current) return;
            setDeviceDetail((prev) => (prev ? {
                ...prev,
                groups: [...(prev.groups ?? []), ...(res?.groups ?? [])],
                nextGroupPageToken: res?.nextGroupPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more device groups:", err);
        } finally {
            if (mountedRef.current) setDeviceGroupsLoading(false);
        }
    }, [selectedDevice, deviceDetail, deviceGroupsLoading]);

    const loadMoreGroupSubGroups = useCallback(async () => {
        if (!selectedGroup || !groupDetail?.nextSubGroupPageToken || groupSubGroupsLoading) return;
        setGroupSubGroupsLoading(true);
        try {
            const res = await getDirectoryGroup({
                sourceProvider: selectedGroup.sourceProvider,
                sourceId: selectedGroup.sourceId,
                subGroupPageToken: groupDetail.nextSubGroupPageToken,
            });
            if (!mountedRef.current) return;
            setGroupDetail((prev) => (prev ? {
                ...prev,
                subGroups: [...(prev.subGroups ?? []), ...(res?.subGroups ?? [])],
                nextSubGroupPageToken: res?.nextSubGroupPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more sub-groups:", err);
        } finally {
            if (mountedRef.current) setGroupSubGroupsLoading(false);
        }
    }, [selectedGroup, groupDetail, groupSubGroupsLoading]);

    const loadMoreGroupDevices = useCallback(async () => {
        if (!selectedGroup || !groupDetail?.nextDevicePageToken || groupDevicesLoading) return;
        setGroupDevicesLoading(true);
        try {
            const res = await getDirectoryGroup({
                sourceProvider: selectedGroup.sourceProvider,
                sourceId: selectedGroup.sourceId,
                devicePageToken: groupDetail.nextDevicePageToken,
            });
            if (!mountedRef.current) return;
            setGroupDetail((prev) => (prev ? {
                ...prev,
                devices: [...(prev.devices ?? []), ...(res?.devices ?? [])],
                nextDevicePageToken: res?.nextDevicePageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more group devices:", err);
        } finally {
            if (mountedRef.current) setGroupDevicesLoading(false);
        }
    }, [selectedGroup, groupDetail, groupDevicesLoading]);

    // ── Cell renderers per section ───────────────────────────────

    const userRenderCell = useCallback((u, col) => {
        switch (col.id) {
            case "displayName": return (
                <OverflowTooltip as="strong" text={u.displayName || "—"}
                    className="text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]" />
            );
            case "email": return (
                <OverflowTooltip as="span" text={u.email || ""}
                    className="text-[11px] leading-[1.18] text-[color:var(--muted)]" />
            );
            case "provider":   return <ProviderBadge provider={u.sourceProvider} />;
            case "department": return <OverflowTooltip as="span" text={u.department || "—"} className="text-[11px] text-[color:var(--muted)]" />;
            case "status":     return <StatusBadge enabled={u.accountEnabled} />;
            case "title":      return <OverflowTooltip as="span" text={u.title || "—"} className="text-[11px] text-[color:var(--muted)]" />;
            default: return null;
        }
    }, []);

    const groupRenderCell = useCallback((g, col) => {
        switch (col.id) {
            case "displayName": return (
                <OverflowTooltip as="strong" text={g.displayName || "—"}
                    className="text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]" />
            );
            case "provider":    return <ProviderBadge provider={g.sourceProvider} />;
            case "memberCount": return <span className="text-[11px] text-[color:var(--ink)] tabular-nums" title={TOTAL_MEMBERS_HINT}>{g.memberCount}</span>;
            case "syncedAt":    return <TimestampCell value={g.syncedAt} />;
            default: return null;
        }
    }, []);

    const deviceRenderCell = useCallback((d, col) => {
        switch (col.id) {
            case "displayName": return (
                <OverflowTooltip as="strong" text={d.displayName || "—"}
                    className="text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]" />
            );
            case "serialNumber": return (
                <OverflowTooltip as="span" text={d.serialNumber || "—"}
                    className="text-[11px] leading-[1.18] font-mono text-[color:var(--muted)]" />
            );
            case "provider": return <ProviderBadge provider={d.sourceProvider} />;
            case "os": {
                return <OverflowTooltip as="span" text={osLabel(d.operatingSystem, d.osVersion) || "—"} className="text-[11px] text-[color:var(--muted)]" />;
            }
            case "owner": {
                const looksHumanReadable = (s) => s.includes("@") || s.startsWith("CN=");
                const refs = d.ownerRef ?? [];
                let resolvedUser = null;
                for (const ref of refs) {
                    const entry = ownerCache[`${d.sourceProvider}:${ref}`];
                    if (entry && entry !== "pending" && entry !== "missing" && entry !== "error") {
                        resolvedUser = entry;
                        break;
                    }
                }
                // Resolved user before readable ref: reversed, a Google row
                // shows an email where an Okta row beside it shows a name.
                const label =
                    d.ownerDisplayName
                    || (resolvedUser?.displayName || resolvedUser?.email || resolvedUser?.upn || "")
                    || refs.find(looksHumanReadable)
                    || refs[0]
                    || "";
                if (!label) return <span className="text-[color:var(--muted-soft)]">—</span>;
                if (resolvedUser) {
                    return (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigateToUser(resolvedUser); }}
                            className="text-left text-[11px] text-[color:var(--ink)] hover:text-[color:var(--green)] cursor-pointer truncate"
                            title={label}
                        >
                            {label}
                        </button>
                    );
                }
                return <OverflowTooltip as="span" text={label} className="text-[11px] text-[color:var(--muted)]" />;
            }
            default: return null;
        }
    }, [ownerCache, navigateToUser]);

    // ── Active section data ──────────────────────────────────────

    const activeCols     = section === "users" ? USER_COLS : section === "groups" ? GROUP_COLS : DEVICE_COLS;
    const activeRows     = section === "users" ? users : section === "groups" ? groups : devices;
    const activeError    = section === "users" ? usersError : section === "groups" ? groupsError : devicesError;
    const activeLoading  = section === "users" ? usersLoading : section === "groups" ? groupsLoading : devicesLoading;
    const activeToken    = section === "users" ? usersToken : section === "groups" ? groupsToken : devicesToken;
    const activeFetch    = section === "users" ? fetchUsers : section === "groups" ? fetchGroups : fetchDevices;
    // Nothing loaded yet: the count holds its place rather than reading "0".
    const activeCount = activeLoading && !activeRows.length ? null : activeRows.length;

    // Scroll pagination. DataTable reports the row band the reader can actually
    // see; once that band reaches the tail, pull the next page. The token is
    // recorded before the fetch so a band that advances again mid-flight cannot
    // request the same page twice.
    const [visibleRange, setVisibleRange] = useState(NO_VISIBLE_RANGE);
    const requestedTokenRef = useRef(null);
    useEffect(() => { requestedTokenRef.current = null; }, [section, provider, search]);
    useEffect(() => {
        if (!visibleRange.measured || !activeToken || activeLoading) return;
        if (requestedTokenRef.current === activeToken) return;
        if (visibleRange.end < activeRows.length - PREFETCH_ROWS) return;
        requestedTokenRef.current = activeToken;
        activeFetch(activeToken);
    }, [visibleRange, activeToken, activeLoading, activeRows.length, activeFetch]);
    const activeRender   = section === "users" ? userRenderCell : section === "groups" ? groupRenderCell : deviceRenderCell;



    const onRowClick = useCallback((row) => {
        if (section === "users") {
            setSelectedUser((cur) => cur?.sourceId === row.sourceId ? null : row);
        } else if (section === "groups") {
            setSelectedGroup((cur) => cur?.sourceId === row.sourceId ? null : row);
        } else {
            setSelectedDevice((cur) => cur?.sourceId === row.sourceId ? null : row);
        }
    }, [section]);

    // ── Drawer state ─────────────────────────────────────────────

    const hasSelection =
        (section === "users"   && !!selectedUser) ||
        (section === "groups"  && !!selectedGroup) ||
        (section === "devices" && !!selectedDevice);

    const cols = useMemo(() => {
        // Flex columns make DataTable fill its parent rather than size to
        // its content, so the tracks below share the list's width.
        const effective = activeCols
            .filter((c) => !c.has || !activeRows.length || activeRows.some(c.has))
            .filter((c) => !hasSelection || c.primary)
            .map((c) => ({ ...c, flex: true }));
        const gridCols = effective.map((c) => `minmax(${c.min}px,${c.grow}fr)`).join(" ");
        return { effective, gridCols, widths: { setWidth: () => {} } };
    }, [activeCols, activeRows, hasSelection]);

    const closeSelection = useCallback(() => {
        setSelectedUser(null);
        setSelectedGroup(null);
        setSelectedDevice(null);
    }, []);

    // Clear only the selections that don't belong to the now-active section.
    // A manual tab switch still drops a stale selection (so it can't resurface
    // when the operator returns to that tab), but a programmatic cross-section
    // navigate (e.g. clicking a group in a user's Groups list) keeps the
    // selection it just set — clearing all three here would race that setter
    // and leave the drawer closed.
    useEffect(() => {
        if (section !== "users") setSelectedUser(null);
        if (section !== "groups") setSelectedGroup(null);
        if (section !== "devices") setSelectedDevice(null);
    }, [section]);

    // ── Headers ──────────────────────────────────────────────────

    // The drawer's head names what is open; its close clears the selection,
    // which collapses the drawer (it is bound to the selection, not
    // toggleable on its own). SplitPage's own close chevron is turned off.
    const detailTitle =
        section === "users"   && selectedUser   ? (selectedUser.displayName || selectedUser.email || selectedUser.sourceId) :
        section === "groups"  && selectedGroup  ? selectedGroup.displayName :
        section === "devices" && selectedDevice ? (selectedDevice.displayName || selectedDevice.sourceId) :
        "";
    const detailHeader = <DetailHeader title={detailTitle} onClose={closeSelection} />;

    // ── Right content (detail body) ──────────────────────────────

    let detailTabs = null;
    let detailBody = null;
    if (section === "users" && selectedUser) {
        detailTabs = USER_DETAIL_TABS;
        detailBody = (
            <UserDetailContent
                selected={selectedUser}
                detail={userDetail}
                loading={userDetailLoading}
                error={userDetailError}
                activeTab={detailTab}
                navigateToGroup={navigateToGroup}
                navigateToDevice={navigateToDevice}
                onLoadMoreGroups={loadMoreUserGroups}
                groupsLoading={userGroupsLoading}
                onLoadMoreDevices={loadMoreUserDevices}
                devicesLoading={userDevicesLoading}
            />
        );
    } else if (section === "groups" && selectedGroup) {
        detailTabs = GROUP_DETAIL_TABS;
        detailBody = (
            <GroupDetailContent
                selected={selectedGroup}
                detail={groupDetail}
                loading={groupDetailLoading}
                error={groupDetailError}
                activeTab={detailTab}
                navigateToGroup={navigateToGroup}
                navigateToUser={navigateToUser}
                navigateToDevice={navigateToDevice}
                onLoadMoreUsers={loadMoreGroupUsers}
                usersLoading={groupUsersLoading}
                onLoadMoreDevices={loadMoreGroupDevices}
                devicesLoading={groupDevicesLoading}
                onLoadMoreSubGroups={loadMoreGroupSubGroups}
                subGroupsLoading={groupSubGroupsLoading}
            />
        );
    } else if (section === "devices" && selectedDevice) {
        detailTabs = DEVICE_DETAIL_TABS;
        detailBody = (
            <DeviceDetailContent
                selected={selectedDevice}
                detail={deviceDetail}
                loading={deviceDetailLoading}
                error={deviceDetailError}
                activeTab={detailTab}
                navigateToUser={navigateToUser}
                navigateToGroup={navigateToGroup}
                onLoadMoreGroups={loadMoreDeviceGroups}
                groupsLoading={deviceGroupsLoading}
            />
        );
    }

    const noun = SECTION_NOUN[section] ?? SECTION_NOUN.users;
    const syncNotice = syncingNotice(integrations, provider, noun.many);

    const leftContent = (
        <div className={`flex h-full min-h-0 min-w-0 flex-col ${hasSelection ? "pr-[16px]" : ""}`}>
            {syncNotice ? (
                <div role="status" className="shrink-0 pb-[8px]">
                    <Notice tone="info">{syncNotice}</Notice>
                </div>
            ) : null}
            <Listing
                className="min-h-0 flex-1"
                count={activeCount}
                error={activeError}
                errorTitle={`Couldn't load ${section === "users" ? "people" : section}`}
                onRetry={() => activeFetch()}
                retrying={activeLoading}
                filtered={Boolean(search || provider)}
                bar={<>
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        placeholder={`Search ${noun.many}…`}
                        label={`Search ${noun.many}`}
                        size="sm"
                        className="w-[260px] max-w-full"
                    />
                    <Select
                        value={provider}
                        onChange={setProvider}
                        options={PROVIDER_OPTIONS}
                        ariaLabel="Filter by provider"
                        className="min-w-[140px]"
                        triggerStyle={{ width: "auto" }}
                    />
                    <Count n={activeCount} one={`${noun.one} shown`} many={`${noun.many} shown`} />
                </>}
                empty={{
                    title: syncNotice ? `No ${noun.many} yet` : `No ${noun.many} found`,
                    // Never tell someone to start a sync that is running.
                    note: syncNotice ? "A sync is running now. Rows appear here as it goes." : "Sync a directory service to fill this list.",
                }}
                noMatch={syncNotice
                    ? `No ${noun.many} yet. A sync is running now. Rows appear here as it goes.`
                    : `No ${noun.many} found. Try clearing the search or provider filter.`}
            >
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <DataTable
                        cols={cols}
                        rows={activeRows}
                        getRowId={(r) => `${r.sourceProvider}:${r.sourceId}`}
                        renderCell={activeRender}
                        onRowClick={onRowClick}
                        loading={activeLoading}
                        skeletonRows={6}
                        onVisibleRange={setVisibleRange}
                    />
                    {activeToken && activeRows.length ? (
                        <div className="shrink-0 border-t border-[color:var(--line-soft)] py-[7px] text-center text-[12px] text-[color:var(--muted)]">
                            {activeLoading ? "Loading more…" : "Scroll for more"}
                        </div>
                    ) : null}
                </div>
            </Listing>
        </div>
    );

    const rightContent = () => (
        <div className="flex h-full min-h-0 min-w-0 flex-col border-l border-[color:var(--line-soft)] pl-[16px]">
            {detailTabs && detailTabs.length > 1 ? (
                <div className="shrink-0 pt-[10px]">
                    <Tabs items={detailTabs} value={detailTab} onChange={setDetailTab} label="Details" />
                </div>
            ) : null}
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto py-[14px]">
                {detailBody}
            </div>
        </div>
    );

    return (
        // Settings frames this browser with --main-inset at zero; elsewhere the
        // page's inset is cancelled here because SplitPage's ContentShell adds
        // the same inset to each column.
        <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
            <SplitPage
                defaultDetailRatio={0.42}
                focusHeader={focusHeader}
                leftContent={leftContent}
                rightHeader={detailHeader}
                rightContent={rightContent}
                // Drawer is bound to the selection: closed when nothing is
                // selected, open whenever something is. DetailHeader's close
                // clears the selection, which flips forceCollapse.
                forceCollapse={!hasSelection}
                onCloseDetail={closeSelection}
                hideDetailClose
            />
        </div>
    );
}
